import React from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import LoadingSpinner from './LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireAdmin?: boolean;
  requireSystemAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requireAdmin = false,
  requireSystemAdmin = false,
}) => {
  const { currentUser, loading: authLoading } = useAuth();
  const location = useLocation();
  
  // ローディング中は早期リターン
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  // AuthContextから直接ロールを取得（型を文字列として扱う）
  const role = currentUser?.role as string | null;
  const isSystemAdmin = role === 'system_admin';
  const isAdminOrAbove = role === 'admin' || role === 'system_admin';

  // 認証が必要で、ユーザーがログインしていない
  if (requireAuth && !currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 管理者権限が必要（admin または system_admin）
  if (requireAdmin && !isAdminOrAbove) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">アクセスが拒否されました</h2>
          <p className="text-gray-600 mb-6">
            このページにアクセスするには管理者権限が必要です。
          </p>
          <p className="text-sm text-gray-500 mb-6">
            現在のロール: {
              role === 'system_admin' ? 'システム管理者' : 
              role === 'admin' ? '管理者' : 
              role === 'user' ? '一般ユーザー' : 
              'なし'
            }
          </p>
          <Link
            to="/app"
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            ホームに戻る
          </Link>
        </div>
      </div>
    );
  }

  // システム管理者権限が必要
  if (requireSystemAdmin && !isSystemAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">アクセスが拒否されました</h2>
          <p className="text-gray-600 mb-6">
            このページにアクセスするにはシステム管理者権限が必要です。
          </p>
          <p className="text-sm text-gray-500 mb-6">
            現在のロール: {
              role === 'admin' ? '管理者' : 
              role === 'user' ? '一般ユーザー' : 
              'なし'
            }
          </p>
          <Link
            to="/app"
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            ホームに戻る
          </Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;